// src/components/PipelineAnalysis/PipelineAnalysis.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import UploadModal from "./upload-modal";
import ParameterSelection from "./parameter-selection";
import './pipeline-analysis.css';
import { getCookieId } from '../../WebserverUser';
import {
  defaultServicePipeline,
  loadServiceCatalogue,
  loadServiceJobs,
  openJobDownload,
  serviceRequest,
} from '../../../shared/xomics-webserver-client';
import WebserverDemoSection from '../../../shared/WebserverDemoSection';
import WebserverReportPanel from '../../../shared/WebserverReportPanel';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';

const POLLING_INTERVAL_MS = 5000; // Poll every 5 seconds
const PROGRESS_SIMULATION_DURATION_MS = 5 * 60 * 1000; // 5 minutes for 0-90%
const SERVICE_CODE = 'oncorisk';

function PipelineAnalysis() {
  const [showJobsList, setShowJobsList] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const selectedJobRef = useRef(null);
  const handleJobSelectRef = useRef(null);
  const [cookieId, setCookieId] = useState('');
  // Report types XOmics binds to the OncoRisk pipeline, from the catalogue.
  const [reportTypes, setReportTypes] = useState([]);
  const [isLoadingJobs, setIsLoadingJobs] = useState(true);
  const [isLoadingJobDetails, setIsLoadingJobDetails] = useState(false); // Separate state for job details
  const [errorLoadingJobs, setErrorLoadingJobs] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isLightTheme, setIsLightTheme] = useState(false);

  const [resultsSearch, setResultsSearch] = useState('');
  const [resultsTypeFilter, setResultsTypeFilter] = useState('all');

  // For forcing re-render to update progress bars
  const [, setTick] = useState(0); 

  useEffect(() => {
    const id = getCookieId();
    console.log('[PipelineAnalysis] cookie from webserverUser:', id);
    if (id) {
      setCookieId(id);
    } else {
      console.warn('[PipelineAnalysis] No cookieId found.');
      setErrorLoadingJobs('User cookie not found. Cannot load analysis jobs.');
      setIsLoadingJobs(false);
    }
  }, []);

  useEffect(() => {
    if (!cookieId) return;
    loadServiceCatalogue(SERVICE_CODE, cookieId)
      .then((catalogue) => setReportTypes(defaultServicePipeline(catalogue).report_types || []))
      .catch((error) => console.error('[PipelineAnalysis] catalogue unavailable:', error));
  }, [cookieId]);

  // Theme detection effect
  useEffect(() => {
    const checkTheme = () => {
      if (typeof document !== 'undefined') {
        const hasLightClass = document.documentElement.classList.contains('ocrp-theme-light');
        setIsLightTheme(hasLightClass);
      }
    };

    checkTheme();

    if (typeof window !== 'undefined') {
      const handleThemeChange = (event) => {
        if (event.detail && typeof event.detail.theme === 'string') {
          setIsLightTheme(event.detail.theme === 'light');
        }
      };

      window.addEventListener('ocrp-theme-change', handleThemeChange);
      return () => window.removeEventListener('ocrp-theme-change', handleThemeChange);
    }
  }, []);

  // Listen for event to open upload modal (from OncoHome quick action)
  useEffect(() => {
    const handleOpenModal = () => {
      setShowUploadModal(true);
    };
    window.addEventListener('ocrp-open-upload-modal', handleOpenModal);
    return () => window.removeEventListener('ocrp-open-upload-modal', handleOpenModal);
  }, []);

  // Keep selectedJobRef in sync with selectedJob state
  useEffect(() => {
    selectedJobRef.current = selectedJob;
  }, [selectedJob]);

const fetchJobs = useCallback(async (currentCookieId, specificJobId = null, isPolling = false) => {
    if (!currentCookieId) return;

    try {
      const response = await loadServiceJobs(SERVICE_CODE, currentCookieId, { limit: 50 });
      const rows = response.jobs.map((job) => ({
        ...job,
        uuid: job.job_uuid || job.upload_uuid,
        name: job.sample_name,
        patientId: 'External submission',
        fileType: job.sample_code,
        pipeline_initiated_time: job.started_at,
        pipelines_completed_time: job.finished_at,
        upload_time: job.created_at,
        error: job.message,
        resultsData: [],
      }));
      let data = rows;

      if (specificJobId) {
        const selected = rows.find((job) => job.uuid === specificJobId);
        if (!selected) return null;
        let outputs = [];
        if (selected.upload_uuid && selected.job_uuid) {
          const outputBody = await serviceRequest(
            SERVICE_CODE,
            currentCookieId,
            `/uploads/${encodeURIComponent(selected.upload_uuid)}`
              + `/jobs/${encodeURIComponent(selected.job_uuid)}/outputs`,
          );
          outputs = outputBody.outputs || [];
        }
        data = { ...selected, outputs };
      }

      // ===============================================
      // ==         THIS IS THE CORRECTED LOGGING       ==
      // ===============================================
      if (specificJobId) {
        console.log("%c--- SINGLE JOB FETCH COMPLETE ---", "color: #28a745; font-weight: bold;");
        console.log("Full Job Object Received from Server:", data);
        
        if (data && data.resultsData) {
            console.log(`Received ${data.resultsData.length} variants in resultsData.`);
            
            // CORRECTED CHECK: Look for `deep_query` and verify it's an object, not the pointer string.
            const variantsWithDeepQuery = data.resultsData.filter(row => typeof row.deep_query === 'object' && row.deep_query !== null);
            
            console.log(`${variantsWithDeepQuery.length} of those variants have a full 'deep_query' object attached.`);
            
            if (variantsWithDeepQuery.length > 0) {
                console.log("Example of a variant with the full deep_query object:", variantsWithDeepQuery[0]);
            }
        } else {
            console.warn("The fetched job data is missing the 'resultsData' array.");
        }

      } else if (Array.isArray(data)) {
        console.log(`[PipelineAnalysis] Job list received with ${data.length} jobs.`);
      }
      // ===============================================
      
      return data;

    } catch (err) {
      console.error(`fetchAnalysisJob error (jobId: ${specificJobId || 'all'}):`, err);
      if (!specificJobId && !isPolling) setErrorLoadingJobs(`Network error: ${err.message}`);
      return null;
    }
  }, [/* Add your dependencies back here, e.g., setErrorLoadingJobs */]);
  // Initial job load and polling
  // Initial job load and polling
  useEffect(() => {
    if (!cookieId) return;

    let isMounted = true;
    let pollTimeoutId = null;
    let isInitialLoad = true;

    const loadAndPoll = async () => {
      if (!isMounted) return;

      if (isInitialLoad) {
        setIsLoadingJobs(true);
      }

      const fetchedJobsData = await fetchJobs(cookieId, null, !isInitialLoad);

      if (!isMounted) return;

      if (isInitialLoad) {
        setIsLoadingJobs(false);
        isInitialLoad = false;
      }

      if (fetchedJobsData && Array.isArray(fetchedJobsData)) {
        const hasJobsChanged = JSON.stringify(fetchedJobsData) !== JSON.stringify(jobs);

        if (hasJobsChanged) {
          console.log('[PipelineAnalysis] Jobs list updated');
          setJobs(fetchedJobsData);
          setErrorLoadingJobs(null);

          if (fetchedJobsData.length > 0) {
            const currentSelectedJob = selectedJobRef.current;
            const currentSelectedExists = currentSelectedJob && fetchedJobsData.find(j => j.uuid === currentSelectedJob.uuid);

            if (!currentSelectedExists) {
              // MODIFIED BLOCK STARTS HERE
              // If a job *was* selected, but it's not in the new list (or list is empty), deselect it.
              // If no job was selected initially (selectedJob was null), selectedJob remains null (no auto-selection).
              if (currentSelectedJob) { // This condition is true only if a job WAS selected but is now gone from the list
                  console.log('[PipelineAnalysis] Previously selected job no longer in list. Deselecting.');
                  setSelectedJob(null);
              }
              // If selectedJob was initially null, this 'if (selectedJob)' is false, and selectedJob remains null.
              // MODIFIED BLOCK ENDS HERE
            } else if (currentSelectedJob) {
              // Check if status changed in the jobs list (which is regularly polled)
              const jobFromList = fetchedJobsData.find(j => j.uuid === currentSelectedJob.uuid);
              if (jobFromList && jobFromList.status !== currentSelectedJob.status) {
                console.log(`[PipelineAnalysis] Status changed from '${currentSelectedJob.status}' to '${jobFromList.status}', re-running job select`);
                if (handleJobSelectRef.current) {
                  handleJobSelectRef.current(jobFromList);
                }
              } else {
                // Re-fetch details for the currently selected job to keep it fresh
                const updatedSelectedJobDetails = await fetchJobs(cookieId, currentSelectedJob.uuid, true);
                if (isMounted && updatedSelectedJobDetails) {
                  if (JSON.stringify(updatedSelectedJobDetails) !== JSON.stringify(currentSelectedJob)) {
                    console.log('[PipelineAnalysis] Selected job details updated');
                    setSelectedJob(updatedSelectedJobDetails);
                  }
                }
              }
            }
          } else if (selectedJobRef.current) { // If fetchedJobsData becomes empty
            console.log('[PipelineAnalysis] Jobs list is now empty. Deselecting job.');
            setSelectedJob(null);
          }
        }
      } else if (fetchedJobsData === null && jobs.length === 0 && isInitialLoad) {
        // Error already set by fetchJobs
      }

      if (isMounted) {
        pollTimeoutId = setTimeout(loadAndPoll, POLLING_INTERVAL_MS);
      }
    };

    loadAndPoll();

    return () => {
      isMounted = false;
      if (pollTimeoutId) clearTimeout(pollTimeoutId);
    };
  }, [cookieId, fetchJobs]);
  // Effect for progress bar updates
  useEffect(() => {
      const hasRunningJobs = jobs.some(job => job.status === 'running');
      if (!hasRunningJobs) return;

      const intervalId = setInterval(() => {
          setTick(prevTick => prevTick + 1); // Force re-render
      }, 1000); // Update progress display every second

      return () => clearInterval(intervalId);
  }, [jobs]); // Rerun when jobs array changes





  const calculateFrontendProgress = useCallback((job) => {
    if (!job) return 0;

    const { status, pipeline_initiated_time, pipelines_completed_time } = job;

    if (status === 'completed') return 100;
    if (status === 'queued' || !pipeline_initiated_time) return 0;

    const initiatedTime = new Date(pipeline_initiated_time).getTime();
    if (isNaN(initiatedTime)) return 0;

    if (status === 'running') {
        const elapsed = Date.now() - initiatedTime;
        const calculatedProgress = (elapsed / PROGRESS_SIMULATION_DURATION_MS) * 100;
        return Math.min(90, Math.max(0, Math.round(calculatedProgress)));
    }

    if (status === 'failed') {
        if (pipelines_completed_time) { // This is the failure time
            const failureTime = new Date(pipelines_completed_time).getTime();
            if (!isNaN(failureTime)) {
                const elapsedAtFailure = failureTime - initiatedTime;
                const calculatedProgress = (elapsedAtFailure / PROGRESS_SIMULATION_DURATION_MS) * 100;
                return Math.min(100, Math.max(0, Math.round(calculatedProgress)));
            }
        }
        return 0; 
    }
    return 0;
  }, []);

  const getStatusColor = (status) => {
    const statusColors = {
      completed: '#4caf50',
      running: '#2196f3',
      failed: '#f44336',
      queued: '#ff9800',
      pending: '#ff9800', // Map 'pending' from DB to 'queued' color
    };
    return statusColors[status] || '#a0a0a0';
  };

  const handleJobSelect = async (job) => {
    setIsLoadingJobDetails(true);
    const detailedJobData = await fetchJobs(cookieId, job.uuid);
    setIsLoadingJobDetails(false);
    if (detailedJobData) {
      setSelectedJob(detailedJobData);
    } else {
      setSelectedJob(job);
      console.error(`Failed to fetch details for job ${job.uuid}. Displaying summary.`);
    }
  };

  // Keep handleJobSelectRef in sync
  handleJobSelectRef.current = handleJobSelect;

  const handleDownload = async () => {
    if (!selectedJob) return;
    try {
      await openJobDownload(SERVICE_CODE, cookieId, selectedJob);
    } catch (error) {
      setErrorLoadingJobs(error.message);
    }
  };

  const handleOutputDownload = async (output) => {
    if (!selectedJob?.upload_uuid || !selectedJob?.job_uuid || !output?.id) return;
    try {
      const body = await serviceRequest(
        SERVICE_CODE,
        cookieId,
        `/uploads/${encodeURIComponent(selectedJob.upload_uuid)}`
          + `/jobs/${encodeURIComponent(selectedJob.job_uuid)}`
          + `/outputs/${encodeURIComponent(output.id)}/download`,
      );
      if (!body.downloadUrl) throw new Error('The output file is not available yet');
      window.open(body.downloadUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      setErrorLoadingJobs(error.message);
    }
  };

  const filteredJobs = useMemo(() => {
    return jobs.filter(job => {
      const matchesSearchTerm = job.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                job.patientId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                job.uuid?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || job.status === statusFilter;
      return matchesSearchTerm && matchesStatus;
    });
  }, [jobs, searchTerm, statusFilter]);

  const renderJobsTab = () => {
    if (isLoadingJobs) { // Only shows on initial load now
      return <div className="OCRP-loading-placeholder">  <i className="fas fa-spinner fa-spin"></i></div>;
    }
    if (errorLoadingJobs && jobs.length === 0) {
      return <div className="OCRP-error-placeholder">Error: {errorLoadingJobs}</div>;
    }

    return (
      <div className="OCRP-jobs-container">
        <div className={`OCRP-jobs-list-container ${!showJobsList ? 'OCRP-panel-collapsed' : ''}`}>
          <div className="OCRP-panel-header">
            {showJobsList && (
              <button
                className="OCRP-upload-open"
                onClick={() => setShowUploadModal(true)}
                title="Upload Files"
              >
                <i className="fas fa-upload"></i> Upload File
              </button>
            )}
            <h2>Analysis Jobs</h2>
            <i className="fas fa-tasks"></i>
          </div>
          <div className="OCRP-jobs-filter">
            <input 
              type="text" 
              placeholder="Search jobs by name, patient, UUID..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">All Statuses</option>
              <option value="running">Running</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
              <option value="queued">Queued</option>
            </select>
          </div>
          <div className="OCRP-jobs-list">
            {filteredJobs.length === 0 && <div className='OCRP-no-jobs-placeholder'> No Jobs Available.</div>}
            {filteredJobs.map(job => {
              const progress = calculateFrontendProgress(job);
              return (
                <div
                  key={job.uuid}
                  className={`OCRP-job-item ${selectedJob && selectedJob.uuid === job.uuid ? 'OCRP-job-selected' : ''}`}
                  onClick={() => handleJobSelect(job)}
                >
                  <div className="OCRP-job-status">
                    <div className="OCRP-status-indicator" style={{ backgroundColor: getStatusColor(job.status) }}></div>
                  </div>
                  <div className="OCRP-job-details">
                    <h3>{job.name || `Job ${job.uuid.substring(0,8)}`}</h3>
                    <div className="OCRP-job-meta">
                      <span className="OCRP-job-patient">Patient/ID: {job.patientId}</span>
                    </div>
                    {job.status === 'running' && (
                      <div className="OCRP-job-progress">
                        <div className="OCRP-progress-bar">
                          <div className="OCRP-progress-fill" style={{ width: `${progress}%`, backgroundColor: getStatusColor(job.status) }}></div>
                        </div>
                        <span>{progress}%</span>
                      </div>
                    )}
                    <div className="OCRP-job-footer-row">
                      <div className="OCRP-job-status-text" style={{ color: getStatusColor(job.status) }}>
                        {job.status === 'running'
                          ? (progress < 40 ? 'Running VEP' : 'Running Deep Query')
                          : job.status.charAt(0).toUpperCase() + job.status.slice(1)}
                      </div>
                      {job.DEMO && <span className="OCRP-job-demo-label">DEMO</span>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="OCRP-job-details-container">
          {isLoadingJobDetails && <div className="OCRP-loading-placeholder">  <i className="fas fa-spinner fa-spin"></i></div>}
          {!isLoadingJobDetails && selectedJob ? (
            <>
              <div className="OCRP-job-header">
                <button
                  className="OCRP-panel-toggle OCRP-toggle-left"
                  onClick={() => setShowJobsList(!showJobsList)}
                  title={showJobsList ? 'Collapse jobs panel' : 'Expand jobs panel'}
                >
                  <i className={`fas fa-chevron-${showJobsList ? 'left' : 'right'}`}></i>
                </button>
                <h2>{selectedJob.name}</h2>
                <div className="OCRP-job-actions" />
              </div>

              <div className="OCRP-job-info-grid">
                <div className="OCRP-info-card">
                  <h4>Job UUID</h4>
                  <p>{selectedJob.uuid}</p>
                </div>
                <div className="OCRP-info-card">
                  <h4>Patient ID</h4>
                  <p>{selectedJob.patientId}</p>
                </div>
                <div className="OCRP-info-card">
                  <h4>Status</h4>
                  <p style={{ color: getStatusColor(selectedJob.status) }}>
                    {selectedJob.status.charAt(0).toUpperCase() + selectedJob.status.slice(1)}
                  </p>
                </div>
                <div className="OCRP-info-card">
                  <h4>Primary File Type</h4>
                  <p>{selectedJob.fileType}</p>
                </div>
                <div className="OCRP-info-card">
                  <h4>Initiated Time</h4>
                  <p>{selectedJob.pipeline_initiated_time ? new Date(selectedJob.pipeline_initiated_time).toLocaleString() : (selectedJob.upload_time ? `(Uploaded: ${new Date(selectedJob.upload_time).toLocaleString()})` : 'N/A')}</p>
                </div>
                {selectedJob.status === 'completed' && selectedJob.pipelines_completed_time && (
                  <div className="OCRP-info-card">
                    <h4>Completion Time</h4>
                    <p>{new Date(selectedJob.pipelines_completed_time).toLocaleString()}</p>
                  </div>
                )}
                {selectedJob.status === 'running' && selectedJob.estimatedCompletion && (
                  <div className="OCRP-info-card">
                    <h4>Est. Completion (Sim)</h4>
                    <p>{new Date(selectedJob.estimatedCompletion).toLocaleString()}</p>
                  </div>
                )}
                {selectedJob.status === 'failed' && (
                  <>
                    {selectedJob.pipelines_completed_time &&
                      <div className="OCRP-info-card">
                        <h4>Failure Time</h4>
                        <p>{new Date(selectedJob.pipelines_completed_time).toLocaleString()}</p>
                      </div>
                    }
                    <div className="OCRP-info-card OCRP-info-card-full-width"> {/* Make error card wider */}
                      <h4>Error Details</h4>
                      <p className="OCRP-error-text">{selectedJob.error || 'No specific error message provided.'}</p>
                    </div>
                  </>
                )}
                {selectedJob.status === 'queued' && (
                  <div className="OCRP-info-card">
                    <h4>Queue Position</h4>
                    <p>{selectedJob.queuePosition || 'N/A'}</p>
                  </div>
                )}
              </div>

              <div className="OCRP-tabs">
                <button className="OCRP-tab-btn OCRP-tab-active">Submission</button>
                {selectedJob.status === 'completed' && (
                  <button className="OCRP-export-button" onClick={handleDownload}>
                    <FontAwesomeIcon icon={faDownload} /> Download all results
                  </button>
                )}
              </div>

              <div className="OCRP-parameters-panel">
                <ParameterSelection
                  selectedJob={selectedJob}
                  resultsData={[]}
                  resultsSearch={resultsSearch}
                  setResultsSearch={setResultsSearch}
                  resultsTypeFilter={resultsTypeFilter}
                  setResultsTypeFilter={setResultsTypeFilter}
                  isLightTheme={isLightTheme}
                />
              </div>

            </>
          ) : (
            !isLoadingJobDetails && <div className="OCRP-no-job-selected">
              <i className="fas fa-tasks"></i>
              <p>Select a job to view details</p>
            </div>
          )}
        </div>

        <div className="OCRP-report-viewer">
          <div className="OCRP-panel-header">
            <h2>Result files</h2>
            <i className="fas fa-folder-open"></i>
          </div>
          <div className="OCRP-report-placeholder">
            <div className="OCRP-report-paper">
              {!selectedJob ? (
                <div className="OCRP-report-message">
                  <div className="OCRP-report-icon">
                    <i className="fas fa-folder-open"></i>
                  </div>
                  <p className="OCRP-report-status-text">Select a job to see its outputs.</p>
                </div>
              ) : selectedJob.status !== 'completed' ? (
                <div className="OCRP-report-message">
                  <div className="OCRP-report-icon"><i className="fas fa-hourglass-half"></i></div>
                  <p className="OCRP-report-status-text">Outputs appear when processing completes.</p>
                </div>
              ) : (selectedJob.outputs || []).length === 0 ? (
                <div className="OCRP-report-message">
                  <div className="OCRP-report-icon"><i className="fas fa-folder-open"></i></div>
                  <p className="OCRP-report-status-text">No output files were declared for this run.</p>
                </div>
              ) : (
                <div className="OCRP-report-gen-panel">
                  {(selectedJob.outputs || []).map((output) => (
                    <button
                      key={output.id}
                      className="OCRP-button"
                      onClick={() => handleOutputDownload(output)}
                    >
                      <i className="fas fa-file-download"></i>
                      <span>{output.filename}</span>
                    </button>
                  ))}
                </div>
              )}
              {selectedJob && (
                <WebserverReportPanel
                  serviceCode={SERVICE_CODE}
                  token={cookieId}
                  job={selectedJob}
                  reportTypes={reportTypes}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  useEffect(() => {
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 300);
  }, [showJobsList]);

  return (
    <div className="OCRP-pipeline-analysis">
      <WebserverDemoSection serviceCode={SERVICE_CODE} />
      <div className="OCRP-analysis-content">
        {renderJobsTab()}
      </div>
      <UploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        cookieId={cookieId}
        onUploadSuccess={() => fetchJobs(cookieId).then(data => data && Array.isArray(data) && setJobs(data))} // Refresh jobs list on successful upload
      />
    </div>
  );
}

export default PipelineAnalysis;
